import { bestRouteMatch } from './bestRouteMatch'
import { Permission } from './permissions'

/**
 * The permission each route requires — declared once, here.
 *
 * Two things read this map and nothing else:
 *   - `<RouteGate />`, mounted once above the logged routes, which renders the
 *     forbidden screen instead of the page. Because it blocks the *mount*, the
 *     page's fetch effects never run, so an admin never issues a request the
 *     API would answer 403.
 *   - the navigation (`useSettingMenuItems`, `MainHeader`), which looks up the
 *     entry's own `url`.
 *
 * That is the point: a menu entry cannot promise a page the admin may not
 * open, because the entry and the page read the same line. The settings menu
 * used to gate on `list admin` as a stand-in for "can see settings", which is
 * how entries ended up pointing at administrator-only and business-only
 * endpoints.
 *
 * Values are the permission the endpoint behind the page actually requires
 * (checked against the API's `Gate::authorize` calls), falling back to the
 * permission the page already declares on its own `<Page permissions>`.
 *
 * Patterns are absolute paths; a `:segment` matches any single segment. The
 * best match wins — most literal segments first, then longest — so
 * `/venues/list` beats `/venues/:venueSlug`, and a subtree entry covers every
 * tab beneath it. `null` means deliberately ungated; an absent path means no
 * gate has been declared yet, and the route is left open.
 */
export const ROUTE_PERMISSIONS: Record<
  string,
  Permission | Permission[] | null
> = {
  // ---------------------------------------------------------------- CRM
  '/users/list': Permission.listUser,
  '/users/transactions': Permission.listReservation,
  // Always reachable by design: existing segments stay readable wherever the
  // criteria endpoints are not deployed yet. Only creating one is gated.
  '/users/segments': null,
  // The detail layout fetches the user and builds the tab strip before any tab
  // renders, so the gate covers the whole subtree. `read user` is what
  // GET /users/{id} requires.
  '/users/:userId': Permission.readUser,
  // The Transactions tab's table is GET /reservations, which authorizes
  // `list reservation`. The membership program manager holds `read user` - so it
  // reaches the detail page above - but not this one, and the tab's mount effect
  // fired the request anyway. Two literal segments beat '/users/:userId''s one,
  // so this entry wins for the tab and the subtree entry still covers the rest.
  '/users/:userId/transactions': Permission.listReservation,
  // The Membership tab opens the membership profile sections, which fetch
  // GET /membership/membership-profiles/{id}; MembershipProfileController
  // authorizes MANAGE_MEMBERS_MEMBERSHIP in its constructor, and marketing and
  // communication hold `read user` but not that.
  '/users/:userId/membership': Permission.manageMembersMembership,

  // ------------------------------------------------------------- Venues
  '/venues/list': Permission.listVenue,
  '/venues/tags': Permission.listTag,
  '/venues/clusters': Permission.listCluster,
  '/venues/:venueSlug': Permission.readVenue,

  // --------------------------------------------------------- Membership
  '/membership/analytics': Permission.manageRequestsMembership,
  // Revenue page and its two drill-downs (subtree). Also behind the
  // `membershipAnalyticsOverview` feature flag, see MembershipRoutes.
  '/membership/revenue': Permission.viewRevenueMembership,
  // Legacy URLs only redirect: left ungated so the redirect target applies its
  // own gate (the permission above) instead of showing a forbidden screen here.
  '/membership/analytics/transactions': null,
  '/membership/analytics/incoming-renewal': null,
  '/membership/events-collections': Permission.manageEventsMembership,
  '/membership/events/list': Permission.manageEventsMembership,
  '/membership/events/:eventId': Permission.manageEventsMembership,
  '/membership/members/list': Permission.manageMembersMembership,
  '/membership/members/:membershipProfileId':
    Permission.manageMembersMembership,

  // ----------------------------------------------------------- Business
  // Business/ReservationController authorizes MANAGE_BUSINESS in its
  // constructor, the new scoped index included. Declared here rather than left
  // to <Page permissions> because this page fetches from a hook effect
  // (useTableUrlSync), which fires on mount even while <Page> is rendering the
  // forbidden screen - only blocking the mount stops the request.
  '/business/reservations': Permission.manageBusiness,

  // ----------------------------------------------------------- Settings
  // Granted to every role (ACCESS_AVAILABILITY_VIEWER names business
  // explicitly); the menu used to demand booking settings, which only
  // administrator and head-of-project hold.
  '/settings/booking/availability-viewer': Permission.accessAvailabilityViewer,
  '/settings/admins': Permission.listAdmin,
  '/settings/admins/roles': Permission.listAdmin,
  '/settings/admins/invites': Permission.createAdmin,
  // GDPR retention is administrator-only. AuthorizationSettingService maps the
  // four gdpr_purge_* setting keys and gdpr_account_retention_warning to
  // CREATE_ADMIN, whose role list is [administrator] alone, so the route gate
  // now says exactly what the endpoints enforce. Gating here rather than only on
  // the page is what matters: the page's fetch effect lives in the page
  // component itself, so it fires even when <Page> renders the forbidden screen
  // - only blocking the mount stops the two doomed requests.
  '/settings/user/data-privacy': Permission.createAdmin,
  '/settings/integrations/sevenrooms': Permission.listAdmin,
  // DominoConfigController authorizes MANAGE_BUSINESS.
  '/settings/integrations/trivec': Permission.manageBusiness,
  '/settings/integrations/adyen': Permission.listAdmin,
  '/settings/integrations/uber': Permission.manageIntegrationUber,
  '/settings/integrations/web-booking': Permission.editSettingsPlatform,
  '/settings/booking/bookings': Permission.manageSettingsBookingSettings,
  '/settings/booking/seating-areas': Permission.listSeatingArea,
  '/settings/booking/booking-notifications':
    Permission.manageSettingsBookingSettings,
  '/settings/booking/payment-link': Permission.listSeatingArea,
  '/settings/bookings/notifications': Permission.manageSettingsBookingSettings,
  '/settings/membership/membership-notifications':
    Permission.manageSettingsMembershipSettings,
  '/settings/membership/programs/list': Permission.manageProgramsMembership,
  '/settings/membership/programs/:programSlug':
    Permission.manageProgramsMembership,
  '/settings/membership/booklets': Permission.manageSettingsMembershipSettings,
  '/settings/membership/participation-statuses':
    Permission.manageSettingsMembershipSettings,
  // MembershipProfileTagController and SubscriptionTagController both
  // authorize MANAGE_MEMBERS_MEMBERSHIP, which the program manager holds.
  '/settings/membership/profiles-tags': Permission.manageMembersMembership,
  '/settings/membership/subscription-tags': Permission.manageMembersMembership,
  // ComplementaryItemController authorizes MANAGE_COMPLEMENTARY_ITEMS.
  '/settings/membership/complementary-items/list':
    Permission.manageComplementaryItems,
  '/settings/business/company-statuses': Permission.manageBusiness,
  '/settings/business/employee-statuses': Permission.manageBusiness,
  '/settings/business/business-notifications': Permission.manageBusiness,
  '/settings/business/verifier-accounts': Permission.manageBusiness,
  '/settings/business/loyalty-tier': Permission.manageBusiness,
  '/settings/platforms': Permission.listPlatform,
  '/settings/platforms/mobile-apps': Permission.listPlatform,
  '/settings/platforms/whatsapp': Permission.listPlatform,
  // ExternalBookingAPIKeyController authorizes MANAGE_API_KEYS.
  '/settings/api/external-booking-api-keys': Permission.manageApiKeys,
  '/settings/legals/legal-documents': Permission.listLegalDocument,
}

/**
 * The permission(s) declared for `path`, or `null` when the path is
 * deliberately ungated, or `undefined` when nothing is declared for it.
 */
export const permissionsForPath = (
  path: string
): Permission | Permission[] | null | undefined =>
  bestRouteMatch(ROUTE_PERMISSIONS, path)
